import React, { useState } from "react";

import { AccountShape, AssetTypes, OwnedRealEstate } from "@eave/shared/types/api";
import {
  FormGroup,
  Loading,
  Modal,
  RadioButton,
  RadioButtonGroup,
  Select,
  SelectItem,
} from "carbon-components-react";
import LabeledObject from "web_modules/shared/LabeledObject";
import APIClient from "web_modules/APIClient";
import { giftRelationshipOptions } from "web_modules/forms/data/relationshipOptions";
import { EMDASH } from "./shared";
import { loadApp } from "../../actions";

interface AccountIdentityProps {
  account: AccountShape;
  onHide: Function;
  dispatch: Function;
  appId: number;
  hasCoBorrower: boolean;
}

function updateAccountIdentity(accountID: number, data: any) {
  return APIClient.put(`/uw/accounts/${accountID}`, data);
}

const OWNERS: { value: string | number; label: string }[] = [
  { value: "null", label: "-" },
  { value: 1, label: "Borrower 1" },
  { value: 2, label: "Borrower 2" },
  { value: 3, label: "Both" },
];

const AccountIdentity: React.FC<AccountIdentityProps> = ({
  account,
  onHide,
  dispatch,
  appId,
  hasCoBorrower,
}: AccountIdentityProps) => {
  const [loading, setLoading] = useState(false);
  const {
    id,
    source_name,
    source_email,
    source_phone,
    source_description,
    source_relationship,
    gift_is_deposited,
  } = account;

  const updateAccountIdentityValues = (key, val) => {
    setLoading(true);

    updateAccountIdentity(id, {
      ...account,
      [key]: val,
    }).then(() => {
      setLoading(false);

      loadApp(dispatch, appId);
    });
  };

  const shouldShowGiftsDepositedRadio = () => {
    if (account.revised_type !== null) {
      // If the revised_type is GiftsTotal, show
      if (account.revised_type === AssetTypes.GiftsTotal) {
        return true;
      }

      // revised_type takes precedence, so if it's not a gift, we shouldn't show gifts.
      return false;
    }

    // There is no revised_type. Simply show if the account is of type GiftsTotal
    if (account.type === AssetTypes.GiftsTotal) {
      return true;
    }
    return false;
  };

  const handleOwnerChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLoading(true);
    const newOwner = JSON.parse(e.target.value) as number | null;
    try {
      if ("operator_ownership_status" in account) {
        const newAccount = account as OwnedRealEstate;
        const newORE = [{ ...newAccount, borrower_entity: newOwner }];
        await APIClient.put("/uw/owned_real_estate", { reos: newORE });
        return;
      }
      await APIClient.put("/accounts_multi_update", {
        accounts: [
          {
            borrower_entity: newOwner,
            id: account.id,
          },
        ],
      });
    } finally {
      setLoading(false);
      loadApp(dispatch, appId);
    }
  };

  const opts = hasCoBorrower ? OWNERS : OWNERS.slice(0, 2);
  return (
    <Modal
      open
      passiveModal // no footer, no buttons
      onBlur={() => onHide()}
      onRequestClose={() => onHide()}
    >
      <div style={{ width: "40%", margin: "1rem" }}>
        {loading && <Loading active />}
        {!loading && (
          <LabeledObject
            size="short"
            title="Account Source"
            listData={[
              {
                label: "Source Name",
                value: source_name || EMDASH,
              },
              {
                label: "Source Email",
                value: source_email || EMDASH,
              },
              {
                label: "Source Phone",
                value: source_phone || EMDASH,
              },
              {
                label: "Source Description",
                value: source_description || EMDASH,
              },
            ]}
          />
        )}

        <Select
          id="borrower-entity"
          size="sm"
          labelText="Select Owner"
          value={account.borrower_entity}
          onChange={handleOwnerChange}
        >
          {opts.map(({ value, label }) => (
            <SelectItem key={label} text={label} value={value} />
          ))}
        </Select>
        <div style={{ marginBottom: "20px" }} />
        <Select
          id="source-relationship"
          size="sm"
          labelText="Select Source Relationship"
          value={source_relationship}
          onChange={e => updateAccountIdentityValues("source_relationship", e.target.value)}
        >
          {giftRelationshipOptions.map(option => (
            <SelectItem key={option.value} text={option.label} value={option.value} />
          ))}
        </Select>
        <div style={{ marginBottom: "20px" }} />
        {account && shouldShowGiftsDepositedRadio() && (
          <FormGroup legendText="Was the gift deposited?">
            <RadioButtonGroup
              id="gift-is-deposited"
              name="gift_is_deposited"
              valueSelected={gift_is_deposited ? "true" : "false"}
              onChange={value => {
                // RadioButtonGroup's `valueSelected` prop doesn't work correctly when using true booleans, so use stringified bools for now
                const val = value === "true";

                updateAccountIdentityValues("gift_is_deposited", val);
              }}
            >
              <RadioButton value="true" labelText="Yes" id="is_deposited" />
              <RadioButton value="false" labelText="No" id="is_not_deposited" />
            </RadioButtonGroup>
          </FormGroup>
        )}
      </div>
    </Modal>
  );
};

export default AccountIdentity;
